import React from 'react'
import {StyledTable, StyledTd, StyledTh} from '../../../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {ApplyTitle, FirstPageDiv, HeaderArea, HeaderAreaBox, NextPageDiv} from './NatAdminApplicatioPrintCommonStyle'
import {HAVE_OR_NOT, RESIDENCE_STYLE} from '../../../../../../CodeMaster'
import {getPrintDate} from '../../helper/NatApplicationHelper'
import {convertToRoomNumberForResident, getFormattedZipCode} from '../../../../../common/util/Natutil'
import {
    NatAbsenceContactAddress,
    NatContactAddressOnEmergency,
    NatContactAddressOnEmergencyDetail,
    NatFamilyStructure,
    NatRentAdminContactAddress
} from '../../../repository/NatGetContactAddressOnEmergency'
import {Nullable} from '../../../../../../app/TypeHelpers'

interface Props {
    contactAddressEmergencyApplyFormParam: NatContactAddressOnEmergencyDetail
}

interface ContactAddressEmergencyForPrinting {
    applyId: string;
    residenceStyle: RESIDENCE_STYLE;
    totalPage: number
    printingDetails: PrintingDetail[]
}

interface PrintingDetail {
    page: number
    contactAddressEmergency?: ContactAddressEmergency
    familyStructure?: NatFamilyStructure[];
}

interface ContactAddressEmergency {
    condominiumName: string;
    ridgeName: string;
    roomNumber: string;
    applyDate: string;
    occupancyDate: string;
    contactAddressOnEmergency: NatContactAddressOnEmergency;
    absenceContactAddress: NatAbsenceContactAddress;
    rentAdminContactAddressFlag: string;
    rentAdminContactAddress: NatRentAdminContactAddress;
}

const FIRST_PAGE_FAMILY_STRUCTURE_COUNT_FOR_UNIT_OWNER = 3
const FIRST_PAGE_FAMILY_STRUCTURE_COUNT_FOR_APPROPRIATOR_AND_TENANT = 2
const FAMILY_STRUCTURE_COUNT_AFTER_SECOND_PAGE = 4

export const NatContactAddressEmergencyApplicationDocument: React.FC<Props> = props => {
    const printData: Nullable<ContactAddressEmergencyForPrinting> =
        generatePrintingDetail(props.contactAddressEmergencyApplyFormParam)

    if (!printData) {
        return null
    }
    return (
        <>
            {printData && printData.printingDetails.map((printDetail, index) => {
                return (
                    <React.Fragment key={index}>
                        {printDetail.page === 1
                            ? renderFirstPage(
                                printData.applyId,
                                printData.residenceStyle,
                                printData.totalPage,
                                printDetail)
                            : renderSecondPageAfter(
                                printData.applyId,
                                printData.residenceStyle,
                                printData.totalPage,
                                printDetail)
                        }
                    </React.Fragment>
                )
            })}
        </>
    )
}

const generatePrintingDetail = (
    contactAddressEmergency: NatContactAddressOnEmergencyDetail
): Nullable<ContactAddressEmergencyForPrinting> => {
    let printingDetails: PrintingDetail[] = []
    const residenceStyle = RESIDENCE_STYLE.codeToEnumConstants(contactAddressEmergency.residenceStyle)

    switch (residenceStyle) {
        case RESIDENCE_STYLE.UNIT_OWNER:
            printingDetails = dividePerContactAddressEmergencyPage(
                contactAddressEmergency,
                FIRST_PAGE_FAMILY_STRUCTURE_COUNT_FOR_UNIT_OWNER
            )
            break
        case RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT:
            printingDetails = dividePerContactAddressEmergencyPage(
                contactAddressEmergency,
                FIRST_PAGE_FAMILY_STRUCTURE_COUNT_FOR_APPROPRIATOR_AND_TENANT
            )
            break
        case RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER:
            printingDetails = [{
                page: 1,
                contactAddressEmergency: contactAddressEmergency
            }]
            break
        default:
            return null
    }
    return {
        applyId: contactAddressEmergency.applyId,
        residenceStyle: residenceStyle,
        totalPage: printingDetails.length,
        printingDetails: printingDetails,
    }
}

const dividePerContactAddressEmergencyPage = (
    contactAddressEmergency: NatContactAddressOnEmergencyDetail,
    firstPageFamilyStructureCount: number
) => {
    const familyStructureView = []

    const familyStructures = [...contactAddressEmergency.familyStructure]
    let pageSize = firstPageFamilyStructureCount
    while (familyStructures.length) {
        familyStructureView.push(familyStructures.splice(0, pageSize))
        pageSize = FAMILY_STRUCTURE_COUNT_AFTER_SECOND_PAGE
    }

    return familyStructureView.map((familyStructure, index) => {
        if (index === 0) {
            return {
                page: index + 1,
                contactAddressEmergency: contactAddressEmergency,
                familyStructure: familyStructure,
            }
        } else {
            return {
                page: index + 1,
                familyStructure: familyStructure,
            }
        }
    })
}

const renderFirstPage = (
    applyId: string,
    residenceStyle: RESIDENCE_STYLE,
    totalPage: number,
    printingDetail: PrintingDetail
) => {
    if (!printingDetail.contactAddressEmergency) {
        return null
    }
    const {
        page, familyStructure
    } = printingDetail
    const {
        applyDate,
        condominiumName,
        ridgeName,
        roomNumber,
        occupancyDate,
        contactAddressOnEmergency,
        absenceContactAddress,
        rentAdminContactAddressFlag,
        rentAdminContactAddress
    } = printingDetail.contactAddressEmergency

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <div>様式：{residenceStyle.label}用</div>
                    <Box ml={'auto'} mt={'auto'}><ApplyTitle>緊急時連絡表</ApplyTitle></Box>
                    <Box ml={'auto'}>
                        <Box>申請ID：{applyId}</Box>
                        <Box textAlign={'right'}>（頁1/{totalPage}）</Box>
                    </Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'2mm 0mm 1mm 150mm'}>
                        {renderDate(applyDate)}
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            <ApplyTable>
                <tbody>
                <tr>
                    <ApplyTableHeading>マンション名</ApplyTableHeading>
                    <ApplyTableCell colSpan={4}>{condominiumName}</ApplyTableCell>
                </tr>
                <tr>
                    <ApplyTableHeading>住戸番号</ApplyTableHeading>
                    <ApplyTableCell colSpan={4}>
                        {ridgeName + ' ' + convertToRoomNumberForResident(roomNumber)}
                    </ApplyTableCell>
                </tr>
                {(residenceStyle === RESIDENCE_STYLE.UNIT_OWNER ||
                        residenceStyle === RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT) &&
                    <tr>
                        <ApplyTableHeading>入居年月日</ApplyTableHeading>
                        <ApplyTableCell colSpan={4}>
                            {renderDate(occupancyDate)}
                        </ApplyTableCell>
                    </tr>
                }
                {renderContactAddressOnEmergency(residenceStyle, contactAddressOnEmergency)}
                {renderAbsenceContactAddress(absenceContactAddress)}
                {renderRentAdminContactAddress(residenceStyle, rentAdminContactAddressFlag, rentAdminContactAddress)
                }
                </tbody>
            </ApplyTable>
            {familyStructure && renderFamilyStructure(residenceStyle, familyStructure, page)}
        </FirstPageDiv>
    </>)
}

const renderSecondPageAfter = (
    applyId: string,
    residenceStyle: RESIDENCE_STYLE,
    totalPage: number,
    printingDetail: PrintingDetail
) => {
    if (!printingDetail.familyStructure) {
        return null
    }
    const {
        page, familyStructure
    } = printingDetail

    return (<>
        <NextPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mb={'1mm'}>
                        <Box>申請ID：{applyId}</Box>
                        <Box textAlign={'right'}>（頁{page}/{totalPage}）</Box>
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            {renderFamilyStructure(residenceStyle, familyStructure, page)}
        </NextPageDiv>
    </>)
}

const renderDate = (date?: string) => {
    if (!date) {
        return null
    }

    const value = getPrintDate(date)
    return (
        <>
            <Box component={'span'} padding={'0 2mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 2mm 0 2mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 2mm 0 2mm'}>{value.day}</Box>日
        </>
    )
}

const renderContactAddressOnEmergency = (
    residenceStyle: RESIDENCE_STYLE,
    contactAddressOnEmergency: NatContactAddressOnEmergency
) => {
    const {
        familyName, firstName, zipCode,
        prefecture, address2, address3, buildingRoomNumber,
        fixedPhoneNumber, mobilePhoneNumber
    } = contactAddressOnEmergency

    return (<>
        {(residenceStyle === RESIDENCE_STYLE.UNIT_OWNER ||
                residenceStyle === RESIDENCE_STYLE.APPROPRIATOR_AND_TENANT) &&
            <tr>
                <ApplyTableHeading>緊急時連絡先</ApplyTableHeading>
                <ApplyTableHeading>固定電話番号</ApplyTableHeading>
                <ApplyTableCell>{fixedPhoneNumber}</ApplyTableCell>
                <ApplyTableHeading>携帯電話番号</ApplyTableHeading>
                <ApplyTableCell>{mobilePhoneNumber}</ApplyTableCell>
            </tr>
        }
        {(residenceStyle === RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER &&
            <>
                <tr>
                    <ApplyTableHeading rowSpan={6}>緊急時連絡先</ApplyTableHeading>
                    <ApplyTableHeading>氏名</ApplyTableHeading>
                    <ApplyTableCell colSpan={3}>{familyName + ' ' + firstName}</ApplyTableCell>
                </tr>
                <tr>
                    <ApplyTableHeading>郵便番号</ApplyTableHeading>
                    <ApplyTableCell>{zipCode && getFormattedZipCode(zipCode, false)}</ApplyTableCell>
                    <ApplyTableHeading>都道府県</ApplyTableHeading>
                    <ApplyTableCell>{prefecture}</ApplyTableCell>
                </tr>
                <tr>
                    <ApplyTableHeading>市区町村</ApplyTableHeading>
                    <ApplyTableCell colSpan={3}>{address2}</ApplyTableCell>
                </tr>
                <tr>
                    <ApplyTableHeading>番地</ApplyTableHeading>
                    <ApplyTableCell colSpan={3}>{address3}</ApplyTableCell>
                </tr>
                <tr>
                    <ApplyTable2RowPartHeightTableHeading>建物名・部屋番号</ApplyTable2RowPartHeightTableHeading>
                    <ApplyTable2RowPartHeightCell
                        colSpan={3}>{buildingRoomNumber}</ApplyTable2RowPartHeightCell>
                </tr>
                <tr>
                    <ApplyTableHeading>固定電話番号</ApplyTableHeading>
                    <ApplyTableCell>{fixedPhoneNumber}</ApplyTableCell>
                    <ApplyTableHeading>携帯電話番号</ApplyTableHeading>
                    <ApplyTableCell>{mobilePhoneNumber}</ApplyTableCell>
                </tr>
            </>
        )}
    </>)
}

const renderAbsenceContactAddress = (absenceContactAddress: NatAbsenceContactAddress) => {
    const {
        familyName, firstName, zipCode,
        prefecture, address2, address3, buildingRoomNumber,
        fixedPhoneNumber, mobilePhoneNumber
    } = absenceContactAddress

    return (<>
        <tr>
            <ApplyTableHeading rowSpan={6}>上記連絡先<br/>不在などの場合の<br/>連絡先</ApplyTableHeading>
            <ApplyTableHeading>氏名</ApplyTableHeading>
            <ApplyTableCell colSpan={3}>{familyName + ' ' + firstName}</ApplyTableCell>
        </tr>
        <tr>
            <ApplyTableHeading>郵便番号</ApplyTableHeading>
            <ApplyTableCell>{getFormattedZipCode(zipCode, false)}</ApplyTableCell>
            <ApplyTableHeading>都道府県</ApplyTableHeading>
            <ApplyTableCell>{prefecture}</ApplyTableCell>
        </tr>
        <tr>
            <ApplyTableHeading>市区町村</ApplyTableHeading>
            <ApplyTableCell colSpan={3}>{address2}</ApplyTableCell>
        </tr>
        <tr>
            <ApplyTableHeading>番地</ApplyTableHeading>
            <ApplyTableCell colSpan={3}>{address3}</ApplyTableCell>
        </tr>
        <tr>
            <ApplyTable2RowPartHeightTableHeading>建物名・部屋番号</ApplyTable2RowPartHeightTableHeading>
            <ApplyTable2RowPartHeightCell
                colSpan={3}>{buildingRoomNumber}</ApplyTable2RowPartHeightCell>
        </tr>
        <tr>
            <ApplyTableHeading>固定電話番号</ApplyTableHeading>
            <ApplyTableCell>{fixedPhoneNumber}</ApplyTableCell>
            <ApplyTableHeading>携帯電話番号</ApplyTableHeading>
            <ApplyTableCell>{mobilePhoneNumber}</ApplyTableCell>
        </tr>
    </>)
}

const renderRentAdminContactAddress = (
    residenceStyle: RESIDENCE_STYLE,
    rentAdminContactAddressFlag: string,
    rentAdminContactAddress: NatRentAdminContactAddress
) => {
    const {
        name, phoneNumber, zipCode,
        prefecture, address2, address3, buildingRoomNumber
    } = rentAdminContactAddress

    if (residenceStyle === RESIDENCE_STYLE.UNIT_OWNER) {
        return (<></>)
    }

    if (rentAdminContactAddressFlag === HAVE_OR_NOT.NOT.value) {
        return (<></>)
    }

    return (<>
        <>
            <tr>
                <ApplyTableHeading rowSpan={6}>賃貸管理者<br/>連絡先<br/>(仲介業者等)</ApplyTableHeading>
                <ApplyTableHeading>名称</ApplyTableHeading>
                <ApplyTableCell colSpan={3}>{name}</ApplyTableCell>
            </tr>
            <tr>
                <ApplyTableHeading>固定電話番号</ApplyTableHeading>
                <ApplyTableCell colSpan={3}>{phoneNumber}</ApplyTableCell>
            </tr>
            <tr>
                <ApplyTableHeading>郵便番号</ApplyTableHeading>
                <ApplyTableCell>{getFormattedZipCode(zipCode, false)}</ApplyTableCell>
                <ApplyTableHeading>都道府県</ApplyTableHeading>
                <ApplyTableCell>{prefecture}</ApplyTableCell>
            </tr>
            <tr>
                <ApplyTableHeading>市区町村</ApplyTableHeading>
                <ApplyTableCell colSpan={3}>{address2}</ApplyTableCell>
            </tr>
            <tr>
                <ApplyTableHeading>番地</ApplyTableHeading>
                <ApplyTableCell colSpan={3}>{address3}</ApplyTableCell>
            </tr>
            <tr>
                <ApplyTable2RowPartHeightTableHeading>建物名・部屋番号</ApplyTable2RowPartHeightTableHeading>
                <ApplyTable2RowPartHeightCell colSpan={3}>{buildingRoomNumber}</ApplyTable2RowPartHeightCell>
            </tr>
        </>
    </>)
}

const renderFamilyStructure = (
    residenceStyle: RESIDENCE_STYLE,
    familyStructure: NatFamilyStructure[],
    page: number
) => {
    if (residenceStyle === RESIDENCE_STYLE.EXTERNAL_UNIT_OWNER) {
        return (<></>)
    }

    return (
        <ApplyTable>
            <FamilyStructureSecondAfterCaption page={page}>家族構成</FamilyStructureSecondAfterCaption>
            <tbody>
            {
                familyStructure.map((family, index) => {
                    const {
                        seq, familyName, firstName, familyNameKana, firstNameKana,
                        relationship, birthDate, workAddress
                    } = family

                    return (<React.Fragment key={index}>
                            <tr>
                                <ApplyTableFamilyStructureCategoryApplyTableHeading
                                    rowSpan={9}>家族{String(seq).toUpperCase()}
                                </ApplyTableFamilyStructureCategoryApplyTableHeading>
                                <ApplyTableFamilyStructureTableHeading>氏名</ApplyTableFamilyStructureTableHeading>
                                <ApplyTableCell colSpan={2}>{familyName + ' ' + firstName}</ApplyTableCell>
                                <ApplyTableHeading>続柄</ApplyTableHeading>
                                <ApplyTableCell>{relationship}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTableFamilyStructureTableHeading>氏名カナ</ApplyTableFamilyStructureTableHeading>
                                <ApplyTableCell colSpan={4}>{familyNameKana + ' ' + firstNameKana}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTableFamilyStructureTableHeading>生年月日</ApplyTableFamilyStructureTableHeading>
                                <ApplyTableCell colSpan={4}>{renderDate(birthDate)}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTableFamilyStructureTableHeading rowSpan={6}>
                                    勤務先
                                </ApplyTableFamilyStructureTableHeading>
                                <ApplyTableCell colSpan={4}>{workAddress.name}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTableHeading>郵便番号</ApplyTableHeading>
                                <ApplyTableCell>{getFormattedZipCode(workAddress.zipCode, false)}</ApplyTableCell>
                                <ApplyTableHeading>都道府県</ApplyTableHeading>
                                <ApplyTableCell>{workAddress.prefecture}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTableHeading>市区町村</ApplyTableHeading>
                                <ApplyTableCell colSpan={3}>{workAddress.address2}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTableHeading>番地</ApplyTableHeading>
                                <ApplyTableCell colSpan={3}>{workAddress.address3}</ApplyTableCell>
                            </tr>
                            <tr>
                                <ApplyTable2RowPartHeightTableHeading>建物名・部屋番号</ApplyTable2RowPartHeightTableHeading>
                                <ApplyTable2RowPartHeightCell
                                    colSpan={3}>{workAddress.buildingRoomNumber}</ApplyTable2RowPartHeightCell>
                            </tr>
                            <tr>
                                <ApplyTableHeading>電話番号</ApplyTableHeading>
                                <ApplyTableCell colSpan={3}>{workAddress.phoneNumber}</ApplyTableCell>
                            </tr>
                        </React.Fragment>
                    )
                })
            }
            </tbody>
        </ApplyTable>
    )
}

const ApplyTable: StyledTable = styled.table`
  width: 190mm;
  border-collapse:collapse;
  font-size: 8pt;
`

const ApplyTableHeading: StyledTh = styled.th`
  width: 32mm;
  height: 5.5mm;
  padding: 0;
  border: 0.1mm #000 solid;
  text-align: center;
  font-weight: 400;
`

const ApplyTableCell: StyledTd = styled.td`
  width: 32mm;
  height: 5.5mm;
  padding: 0 0 0 1mm;
  border: 0.1mm #000 solid;
`

const ApplyTable2RowPartHeightTableHeading = styled(ApplyTableHeading)`
  height: 11mm;
`

const ApplyTable2RowPartHeightCell = styled(ApplyTableCell)`
  height: 11mm;
`

const ApplyTableFamilyStructureCategoryApplyTableHeading = styled(ApplyTableHeading)`
  width: 3mm;
`

const ApplyTableFamilyStructureTableHeading = styled(ApplyTableHeading)`
  width: 27mm;
`

const FamilyStructureCaption = styled.caption`
  border-right: 0.1mm #000 solid;
  border-left: 0.1mm #000 solid;
`

const FamilyStructureSecondAfterCaption = styled(FamilyStructureCaption)<{ page: number }>`
　letter-spacing: 2.5mm;
 ${props => props.page !== 1 ?
    `
    border-top: 0.1mm #000 solid;
   ` : ``}
`
